/**
 * The pure half of «کشورها» (PM-0218): what a column of a country profile SAYS,
 * what an edit CHANGES, and what a save SWITCHES OFF. No React and no `t`, so
 * every rule here is tested without rendering.
 *
 * The rules (docs/country-config/CONTRACT.md §2, REGISTRY-PROTO.md §3):
 *   • a DEFAULT column that is NULL follows `*`; NULL in `*` itself is whatever
 *     the column's consumer does with no value — for some that is today, for the
 *     dial code it is "a number must carry +" (the screen words each one).
 *     `weekendDays` is the one default list, and a stored [] there is "no
 *     weekend", not "follows *";
 *   • a CAPABILITY is authoritative: NULL or empty is NONE, never `*`'s;
 *   • an edit sends only the columns it changed, with the `updatedAt` it loaded:
 *     the BFF re-reads the row and sends the full replace, and refuses a row that
 *     moved meanwhile — so the second of two operators gets CONFLICT and
 *     re-applies, rather than silently undoing the first;
 *   • a save that changes what clinics DO today — a capability taken away, a
 *     gated default cleared or changed — is confirmed item by item, and the BFF
 *     refuses it otherwise (`confirmationsFor`).
 */
import { toAsciiDigits } from '../i18n';
import {
  CAPABILITY_FIELDS, FALLBACK_COUNTRY, GATED_DEFAULT_FIELDS, INSURANCE_CATALOGS, MAX_ZOOM, columnOf, fieldSpec,
} from '../constants/countryProfile';

export const isNull = (v) => v === null || v === undefined;
const isEmptyList = (v) => Array.isArray(v) && v.length === 0;

/**
 * What one column of a STORED row says:
 *   { state: 'value', value }        a value of its own (a [] weekend included)
 *   { state: 'none' }                a capability that is NULL or empty
 *   { state: 'follows', resolved }   a NULL default in a country's row; `resolved`
 *                                    is `*`'s value, null when `*` is NULL too
 *   { state: 'serviceDefault' }      a NULL default in `*` itself, or a NULL left
 *                                    after a merge — the services' own, today
 */
export function describeColumn(spec, row, fallback = null) {
  const v = row ? row[spec.field] : null;
  if (spec.kind === 'capability') {
    return isNull(v) || isEmptyList(v) ? { state: 'none' } : { state: 'value', value: v };
  }
  if (!isNull(v)) return { state: 'value', value: v };
  if (!row || row.country === FALLBACK_COUNTRY || !fallback) return { state: 'serviceDefault' };
  const resolved = fallback[spec.field];
  return { state: 'follows', resolved: isNull(resolved) ? null : resolved };
}

/** The same for a MERGED profile — nothing left to follow: a NULL default is the services' own. */
export function describeMerged(spec, profile) {
  return describeColumn(spec, profile ? { ...profile, country: FALLBACK_COUNTRY } : null, null);
}

// ── the editor ────────────────────────────────────────────────────────────────

const STRING_TYPES = ['enum', 'language', 'currency', 'timezone', 'dialCode', 'key'];

/**
 * A decimal typed in any script or convention → a JS number string: Persian
 * digits and the Persian decimal separator (U+066B), a decimal comma, a
 * typographic minus (U+2212) or dash (U+2013).
 */
export function normaliseDecimal(text) {
  return toAsciiDigits(String(text ?? '').trim()).replace(/[\u066B,]/g, '.').replace(/[\u2212\u2013]/g, '-');
}

/** The editor's state for one group, from the stored row. '' = NULL for a single value. */
export function draftFrom(fields, row) {
  const draft = {};
  for (const spec of fields) {
    const v = row ? row[spec.field] : null;
    if (spec.type === 'weekdays') {
      draft[spec.field] = { follow: isNull(v), days: Array.isArray(v) ? [...v] : [] };
    } else if (spec.type === 'mapView') {
      draft[spec.field] = isNull(v)
        ? { follow: true, lat: '', lon: '', zoom: '' }
        : { follow: false, lat: String(v.lat), lon: String(v.lon), zoom: String(v.zoom) };
    } else if (spec.type === 'list') {
      draft[spec.field] = Array.isArray(v) ? [...v] : [];
    } else if (spec.type === 'weekday') {
      draft[spec.field] = isNull(v) ? '' : String(v);
    } else {
      draft[spec.field] = isNull(v) ? '' : String(v);
    }
  }
  return draft;
}

/** The value a draft field stands for, in the GraphQL shape (null = NULL). */
export function valueOf(spec, d) {
  if (spec.type === 'weekdays') return d.follow ? null : [...new Set(d.days.map(Number))].sort((a, b) => a - b);
  if (spec.type === 'mapView') {
    if (d.follow) return null;
    return { lat: Number(normaliseDecimal(d.lat)), lon: Number(normaliseDecimal(d.lon)), zoom: Number(normaliseDecimal(d.zoom)) };
  }
  if (spec.type === 'list') return (spec.options || []).filter((o) => d.includes(o)).concat(d.filter((o) => !(spec.options || []).includes(o)));
  if (spec.type === 'weekday') return d === '' ? null : Number(d);
  // A dial code typed in Persian or Arabic-Indic digits is the same digits.
  const s = (spec.type === 'dialCode' ? toAsciiDigits(String(d ?? '')) : String(d ?? '')).trim();
  return s === '' ? null : s;
}

/** Two column values are the same; for a capability list, the same SET (and NULL = [] = none). */
export function sameValue(spec, a, b) {
  if (spec.type === 'list') {
    const x = new Set(a || []);
    const y = new Set(b || []);
    return x.size === y.size && [...x].every((v) => y.has(v));
  }
  if (isNull(a) || isNull(b)) return isNull(a) && isNull(b);
  if (spec.type === 'weekdays') return [...a].sort().join(',') === [...b].sort().join(',');
  if (spec.type === 'mapView') return a.lat === b.lat && a.lon === b.lon && a.zoom === b.zoom;
  return String(a) === String(b);
}

/** The `changes` the save sends: only the fields of the group whose value moved. */
export function changesFrom(fields, draft, row) {
  const changes = {};
  for (const spec of fields) {
    const next = valueOf(spec, draft[spec.field]);
    if (!sameValue(spec, row ? row[spec.field] : null, next)) changes[spec.field] = next;
  }
  return changes;
}

const DIAL = /^[1-9][0-9]{0,2}$/;
const LANGUAGE = /^[a-z]{2,3}$/;
const CURRENCY = /^[A-Z]{3}$/;

export function isTimeZone(zone) {
  try {
    // eslint-disable-next-line no-new
    new Intl.DateTimeFormat('en-US', { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/**
 * The fields of a draft that cannot be saved, as `{ field: reasonKey }` — the
 * same shapes the service refuses (INVALID_COUNTRY_PROFILE), caught before the
 * round trip. A NULL is always valid.
 */
export function validateDraft(fields, draft) {
  const errors = {};
  for (const spec of fields) {
    const d = draft[spec.field];
    if (spec.type === 'mapView') {
      if (d.follow) continue;
      const lat = Number(normaliseDecimal(d.lat));
      const lon = Number(normaliseDecimal(d.lon));
      const zoom = Number(normaliseDecimal(d.zoom));
      const bad = [d.lat, d.lon, d.zoom].some((x) => String(x ?? '').trim() === '')
        || !Number.isFinite(lat) || lat < -90 || lat > 90
        || !Number.isFinite(lon) || lon < -180 || lon > 180
        || !Number.isInteger(zoom) || zoom < 0 || zoom > MAX_ZOOM;
      if (bad) errors[spec.field] = 'mapView';
      continue;
    }
    if (spec.type === 'weekdays') {
      // Seven days off is no weekend at all — the subscription reads it as today's rule.
      if (!d.follow && new Set(d.days.map(Number)).size >= 7) errors[spec.field] = 'weekendAll';
      continue;
    }
    if (!STRING_TYPES.includes(spec.type)) continue;
    const v = valueOf(spec, d);
    if (v === null) continue;
    if (spec.type === 'dialCode' && !DIAL.test(v)) errors[spec.field] = 'dialCode';
    if (spec.type === 'timezone' && !isTimeZone(v)) errors[spec.field] = 'timezone';
    if (spec.type === 'language' && !LANGUAGE.test(v)) errors[spec.field] = 'language';
    if (spec.type === 'currency' && !CURRENCY.test(v)) errors[spec.field] = 'currency';
  }
  return errors;
}

// ── what a save changes for clinics, to be confirmed ──────────────────────────

/**
 * A column's value as the part of a confirmation token after `column:` — NULL
 * `null`, a list its items joined by `,` (an empty list is empty), a map view
 * `lat,lon,zoom`. MIRRORS the admin BFF's `tokenValue`: the tokens must match.
 */
export function tokenValue(v) {
  if (isNull(v)) return 'null';
  if (Array.isArray(v)) return v.join(',');
  if (typeof v === 'object') return `${v.lat},${v.lon},${v.zoom}`;
  return String(v);
}

/**
 * Every capability VALUE the row has and the row-after-the-change has not, as
 * `{ field, column, value, token }` (token = `column:value`, what the BFF asks
 * to be confirmed). MIRRORS the admin BFF's `capabilityRemovals`: a capability
 * list loses each value it drops; a single value that changes loses its old one.
 */
export function capabilityRemovals(row, changes) {
  const out = [];
  for (const spec of CAPABILITY_FIELDS) {
    if (!changes || !Object.prototype.hasOwnProperty.call(changes, spec.field)) continue;
    const was = row ? row[spec.field] : null;
    const now = changes[spec.field];
    const lost = Array.isArray(was)
      ? was.filter((v) => !(Array.isArray(now) ? now : []).includes(v))
      : (!isNull(was) && was !== now ? [was] : []);
    const column = spec.column || columnOf(spec.field);
    for (const value of lost) out.push({ field: spec.field, column, value, token: `${column}:${value}` });
  }
  return out;
}

/**
 * Every GATED default the change moves — cleared, set or changed — as
 * `{ field, column, from, to, token }` with token = `column:<the value it had>`.
 * MIRRORS the admin BFF's `gatedDefaultChanges`; NULL and [] differ here (an
 * empty weekend is "no weekend").
 */
export function gatedDefaultChanges(row, changes) {
  const out = [];
  for (const field of GATED_DEFAULT_FIELDS) {
    if (!changes || !Object.prototype.hasOwnProperty.call(changes, field)) continue;
    const from = row ? row[field] : null;
    const to = changes[field];
    if (tokenValue(from) === tokenValue(to) && isNull(from) === isNull(to)) continue;
    const column = columnOf(field);
    out.push({ field, column, from: isNull(from) ? null : from, to: isNull(to) ? null : to, token: `${column}:${tokenValue(from)}` });
  }
  return out;
}

/**
 * Everything a save must have confirmed — capability values taken away, then
 * gated defaults moved — each with `kind` 'removal' | 'change'. Empty = the save
 * goes straight through.
 */
export function confirmationsFor(row, changes) {
  return [
    ...capabilityRemovals(row, changes).map((r) => ({ ...r, kind: 'removal' })),
    ...gatedDefaultChanges(row, changes).map((c) => ({ ...c, kind: 'change', spec: fieldSpec(c.field) })),
  ];
}

// ── the deployment ceiling ────────────────────────────────────────────────────

/** `{ iranianValues, offHere }` of one capability on this deployment (the BFF's `deployment.ceilings`). */
export function ceilingOf(deployment, spec) {
  const column = spec.column || columnOf(spec.field);
  const found = (deployment?.ceilings || []).find((c) => c.capability === column);
  return { iranianValues: found?.iranianValues || [], offHere: found?.offHere || [] };
}

/** The values of a capability as a list (a single value or a list; none = []). */
export const capabilityValues = (v) => (isNull(v) ? [] : (Array.isArray(v) ? v : [v]));

// ── the insurance catalogue a profile may name (PM-0246) ─────────────────────

/**
 * The countries with insurer rows in accounting's catalogue, from a
 * `countryInsurers` answer — inactive rows included, self-pay left out (it
 * belongs to every catalogue, so it names none). null = it could not be read
 * (no answer, or no accounting client on this BFF).
 */
export function insurerCountriesOf(list) {
  if (!list?.configured) return null;
  return [...new Set((list.insurers || []).filter((r) => !r.isSelfPay && r.country).map((r) => r.country))];
}

/**
 * The catalogue keys the profile of `country` may name in `insuranceCatalog`:
 * its own code first — even with no insurer yet, the operator may add them
 * next — then every country that has insurer rows and whatever is stored,
 * deduped, in code order. `insurerCountries` null = the catalogue could not be
 * read: IR and IQ stand in for it. The `*` row has no code of its own. The
 * service takes any assigned ISO code there; this only decides what is offered.
 */
export function insuranceCatalogChoices(country, insurerCountries, stored) {
  const own = country && country !== FALLBACK_COUNTRY ? country : null;
  const others = new Set([...(insurerCountries ?? INSURANCE_CATALOGS), ...(stored ? [stored] : [])]);
  if (own) others.delete(own);
  return [...(own ? [own] : []), ...[...others].sort()];
}
